/**
 * @brief 收到 MQTT 指令后的派发层
 * @note 纯函数，不依赖 React hook；由 useCommon 在 join 后一次性挂载
 */

import store from '@/store';
import {
  setAnswerMode,
  setPendingBroadcastText,
  setExternalLlm,
  setTtsVoice,
  setHistoryMsg,
} from '@/store/slices/room';
import RtcClient from '@/lib/RtcClient';
import MqttClient from '@/lib/MqttClient';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/handler';
import logger from '@/utils/logger';
import { onUserUtteranceFinal } from '@/lib/turnRouter';
import type { CmdEnvelope } from './commands';

const speakNow = (text: string) => {
  const state = store.getState().room;
  const botName = state.sceneConfigMap[state.scene]?.botName;
  if (!botName) {
    logger.debug('[cmd] speakNow: botName 未就绪');
    return;
  }
  RtcClient.commandAgent({
    agentName: botName,
    command: COMMAND.INTERRUPT,
    interruptMode: INTERRUPT_PRIORITY.HIGH,
  });
  RtcClient.commandAgent({
    agentName: botName,
    command: COMMAND.EXTERNAL_TEXT_TO_SPEECH,
    interruptMode: INTERRUPT_PRIORITY.HIGH,
    message: text,
  });
};

const interrupt = () => {
  const state = store.getState().room;
  const botName = state.sceneConfigMap[state.scene]?.botName;
  if (!botName) return;
  RtcClient.commandAgent({
    agentName: botName,
    command: COMMAND.INTERRUPT,
    interruptMode: INTERRUPT_PRIORITY.HIGH,
  });
};

const dispatchCmd = (cmd: CmdEnvelope) => {
  logger.debug('[cmd] received', cmd.type, cmd.payload);
  switch (cmd.type) {
    case 'set_mode': {
      const p = cmd.payload as { mode: 'doubao-llm' | 'external-llm' | 'text-broadcast' };
      store.dispatch(setAnswerMode(p.mode));
      break;
    }
    case 'broadcast_text': {
      const p = cmd.payload as { text: string; speakNow?: boolean };
      if (p.speakNow) {
        speakNow(p.text);
      } else {
        store.dispatch(setPendingBroadcastText(p.text));
      }
      break;
    }
    case 'switch_llm': {
      store.dispatch(setExternalLlm(cmd.payload as any));
      break;
    }
    case 'user_text': {
      const p = cmd.payload as { text: string };
      const text = p.text?.trim();
      if (!text) break;

      const state = store.getState().room;
      const botName = state.sceneConfigMap[state.scene]?.botName;
      const localUser = state.localUser.userId || state.localUser.username || 'user';
      if (!botName) {
        logger.debug('[cmd] user_text: botName 未就绪');
        break;
      }

      store.dispatch(
        setHistoryMsg({
          text,
          user: localUser,
          paragraph: true,
          definite: true,
        })
      );

      if (state.answerMode === 'doubao-llm') {
        RtcClient.commandAgent({
          agentName: botName,
          command: COMMAND.EXTERNAL_TEXT_TO_LLM,
          interruptMode: INTERRUPT_PRIORITY.HIGH,
          message: text,
        });
      } else {
        onUserUtteranceFinal(text);
      }
      break;
    }
    case 'switch_voice': {
      // Phase 4 会再加上 UpdateVoiceChat 调用；这里先落状态
      store.dispatch(setTtsVoice(cmd.payload as any));
      // 动态导入避免循环依赖
      import('@/lib/voiceHotSwitch')
        .then((m) => m.applyVoice(cmd.payload as any))
        .catch(() => {
          /* Phase 4 未到时 noop */
        });
      break;
    }
    case 'interrupt':
      interrupt();
      break;
    case 'inject_history': {
      const p = cmd.payload as { role: 'user' | 'assistant'; text: string };
      const state = store.getState().room;
      const botName = state.sceneConfigMap[state.scene]?.botName || 'bot';
      const localUser = state.localUser.userId || 'user';
      store.dispatch(
        setHistoryMsg({
          text: p.text,
          user: p.role === 'user' ? localUser : botName,
          paragraph: true,
          definite: true,
        })
      );
      break;
    }
    default:
      logger.debug('[cmd] unknown type', (cmd as any).type);
  }
};

let unbind: (() => void) | null = null;

export const attachCmdHandler = () => {
  if (unbind) return;
  unbind = MqttClient.onCmd(dispatchCmd);
};

export const detachCmdHandler = () => {
  if (unbind) {
    unbind();
    unbind = null;
  }
};
